// FAQ content for Cultiv AI Studio.
// Each answer is written to be definitive, 50-120 words, and citable by
// AI search engines (ChatGPT, Perplexity, Google AI Overviews). Keep the
// brand name "Cultiv AI Studio" in answers where it strengthens entity
// association. Consumed by <FAQ /> and serialized into FAQPage JSON-LD.

export const homepageFaqs = [
  {
    id: 'home-what-is-cultiv',
    question: 'What does Cultiv AI Studio do?',
    answer:
      'Cultiv AI Studio is an AI-first creative agency that produces viral video, image, and brand campaigns for modern marketing teams. We combine generative AI tools with senior creative direction to deliver ad creatives, UGC-style video, brand systems, and product visuals in days instead of months. Our work is used by B2B SaaS companies, DTC brands, and early-stage startups that need high-volume, on-brand creative without the overhead of a traditional production agency.',
  },
  {
    id: 'home-services',
    question: 'What services do you offer?',
    answer:
      'Cultiv AI Studio offers four core services: AI ad creative production (static and video ads for Meta, TikTok, YouTube, and LinkedIn), AI UGC and spokesperson video, brand identity systems (logo, type, color, guidelines), and AI-generated UI and product visuals for web and app launches. Every engagement pairs AI production pipelines with a human creative director, so output stays on brand while moving 10x faster than traditional studios.',
  },
  {
    id: 'home-process',
    question: 'How does your AI creative process work?',
    answer:
      'Our process has four stages: strategy, prompt architecture, iteration, and delivery. We start with a brief and audience research, then build reusable prompt systems tuned to your brand voice and visual language. We iterate quickly with AI models for image, video, and copy, and a human creative director reviews every output before delivery. Clients typically see first drafts within 48 hours and final assets within a week.',
  },
  {
    id: 'home-pricing',
    question: 'How does Cultiv AI Studio price engagements?',
    answer:
      'Cultiv AI Studio works on flat-fee project pricing and monthly creative retainers. Project fees depend on scope — a single ad batch starts lower than a full brand system or launch campaign. Retainers are ideal for teams that need 20 to 100+ creative variants per month. We do not bill hourly or charge production surcharges. Book a free strategy call and we will scope a price against your goals on the same call.',
  },
  {
    id: 'home-timeline',
    question: 'How fast can you deliver AI creative?',
    answer:
      'Most Cultiv AI Studio projects deliver first drafts in 48 hours and final assets within 5 to 10 business days. Single ad batches can turn around in under a week; full brand systems and multi-channel launch campaigns run 2 to 4 weeks. Because our pipeline is AI-native, iteration cycles are hours instead of days — so you can test 20 variants in the time a traditional agency produces one.',
  },
  {
    id: 'home-vs-agency',
    question: 'How is Cultiv AI Studio different from a traditional creative agency?',
    answer:
      'Traditional agencies price around shoot days, revision rounds, and billable hours. Cultiv AI Studio prices around outcomes and uses AI to eliminate 80% of the production overhead. You get more variants, faster iteration, and lower cost per asset, without sacrificing creative direction. We keep a senior human creative director on every engagement, so the output reads as premium and on-brand — not as generic AI slop.',
  },
];

export const contactFaqs = [
  {
    id: 'contact-how-to-start',
    question: 'How do I start a project with Cultiv AI Studio?',
    answer:
      'Book a free 30-minute strategy call through the scheduler on this page. On the call we review your goals, current creative workflow, and target channels, then send a written scope and quote within two business days. If it is a fit, we kick off within a week. There is no obligation from the call itself — it is genuinely a working session, not a sales pitch.',
  },
  {
    id: 'contact-what-info',
    question: 'What information should I bring to the first call?',
    answer:
      'Come with your goals (campaign, launch, always-on creative), target audience, rough budget range, and any existing brand assets or ads you want to iterate on. Links to your website, current ad account, and one or two reference pieces you admire are ideal. Cultiv AI Studio does not require a finished brief — a lot of our best engagements start from a rough hypothesis we refine together.',
  },
  {
    id: 'contact-startups',
    question: 'Do you work with startups and early-stage companies?',
    answer:
      'Yes. A large share of Cultiv AI Studio clients are seed to Series B startups that need brand, ads, and launch creative without full in-house teams. We scope engagements to match early-stage budgets and prioritize creative that drives measurable pipeline or activation. If you are pre-revenue or bootstrapped, ask about our compact launch package on the intro call.',
  },
  {
    id: 'contact-timelines',
    question: 'What are typical project timelines?',
    answer:
      'Most projects start within 5 business days of signing. Ad creative batches ship in a week, brand systems in 2 to 3 weeks, and full launch campaigns in 3 to 4 weeks. Retainers operate on weekly sprint cycles with new assets delivered every Friday. Cultiv AI Studio runs AI-native pipelines, so turnarounds are dramatically faster than traditional studios.',
  },
];

export const aiCreativesFaqs = [
  {
    id: 'creatives-tools',
    question: 'What AI tools power the creatives in this gallery?',
    answer:
      'The Cultiv AI Studio gallery features work produced with a stack of leading generative AI tools, including Midjourney, Google Gemini (Imagen and Veo), Runway, Kling, Stable Diffusion, and Pixnova, combined with traditional design tools for composition, typography, and finishing. Every asset is directed by a human creative lead who writes prompts, curates iterations, and finalizes output — the AI is a production tool, not the creative director.',
  },
  {
    id: 'creatives-licensing',
    question: 'Can I license or reuse the creatives shown here?',
    answer:
      'The creatives in this gallery are owned by Cultiv AI Studio and shown as portfolio examples only — they are not licensed for reuse. If you see a style, format, or concept you want for your own brand, we can produce custom AI creatives in a similar direction that you own outright. All client work delivered by Cultiv AI Studio transfers full usage and commercial rights to the client on project completion.',
  },
  {
    id: 'creatives-commission',
    question: 'Can I commission similar AI creatives for my brand?',
    answer:
      'Yes. Cultiv AI Studio takes commissions for AI video, AI portraits, brand visuals, 3D product renders, and campaign imagery in any of the styles shown in this gallery. We start with a brief and reference picks from the gallery, then tune prompts, models, and post-production to match your brand system. Book a strategy call to scope a custom creative run for your next campaign.',
  },
];
